import React, { useMemo, useCallback, useImperativeHandle, forwardRef, useRef } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import { ModuleRegistry } from "@ag-grid-community/core";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import type { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import { useTranslation } from "react-i18next";
import type { CompletionPart } from "./hooks/useCompletionDashboard";
import { pct, coverageColor } from "./utils/coverage";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

export type BoothFlatGridHandle = {
  exportCsv: () => void;
};

type Props = {
  parts: CompletionPart[];
  partToGroup: Record<string, string>;
  searchText: string;
};

type FlatRow = CompletionPart & { area: string; coverage: number };

function downloadCsv(rows: FlatRow[], t: (k: string) => string) {
  const headers = [
    t("booth"), t("area"),
    t("voters") + " (Visited)", t("voters") + " (Total)",
    t("houses") + " (Visited)", t("houses") + " (Total)",
    t("coverage") + " %",
  ];
  const csvRows = rows.map((r) =>
    [r.part, `"${(r.area || "").replace(/"/g, '""')}"`,
     r.visited, r.total, r.visited_families, r.total_families, r.coverage].join(",")
  );
  const csv = [headers.join(","), ...csvRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "voter_coverage_export.csv";
  a.click();
  URL.revokeObjectURL(url);
}

function CoverageCell({ value }: ICellRendererParams<FlatRow, number>) {
  const v = value ?? 0;
  const color = coverageColor(v);
  return (
    <div className="flex items-center gap-2 h-full">
      <div className="w-16 h-2 bg-[#f3f4f6] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${v}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-semibold tabular-nums" style={{ color }}>
        {v}%
      </span>
    </div>
  );
}

export const BoothFlatGrid = forwardRef<BoothFlatGridHandle, Props>(
  function BoothFlatGrid({ parts, partToGroup, searchText }, ref) {
  const { t } = useTranslation("voter_seg_status");
  const gridRef = useRef<AgGridReact<FlatRow>>(null);

  const rows: FlatRow[] = useMemo(
    () =>
      parts.map((p) => ({
        ...p,
        area: partToGroup[p.part] || "",
        coverage: pct(p.visited_families, p.total_families),
      })),
    [parts, partToGroup]
  );

  const exportCsv = useCallback(() => {
    downloadCsv(rows, t);
  }, [rows, t]);

  useImperativeHandle(ref, () => ({ exportCsv }), [exportCsv]);

  const columnDefs = useMemo<ColDef<FlatRow>[]>(
    () => [
      { headerName: t("booth"), field: "part", width: 90, sortable: true },
      { headerName: t("area"), field: "area", flex: 2, minWidth: 180, sortable: true },
      {
        headerName: t("voters"),
        colId: "voters",
        width: 130,
        sortable: true,
        valueGetter: (p) => p.data?.visited ?? 0,
        cellRenderer: (p: ICellRendererParams<FlatRow>) => {
          const d = p.data;
          if (!d) return null;
          return (
            <span className="tabular-nums text-sm">
              <span className="font-medium">{d.visited.toLocaleString()}</span>
              <span className="text-[#9ca3af]"> / {d.total.toLocaleString()}</span>
            </span>
          );
        },
        comparator: (_a, _b, nodeA, nodeB) =>
          (nodeA.data?.visited ?? 0) - (nodeB.data?.visited ?? 0),
      },
      {
        headerName: t("houses"),
        colId: "houses",
        width: 130,
        sortable: true,
        valueGetter: (p) => p.data?.visited_families ?? 0,
        cellRenderer: (p: ICellRendererParams<FlatRow>) => {
          const d = p.data;
          if (!d) return null;
          return (
            <span className="tabular-nums text-sm">
              <span className="font-medium">{d.visited_families.toLocaleString()}</span>
              <span className="text-[#9ca3af]"> / {d.total_families.toLocaleString()}</span>
            </span>
          );
        },
        comparator: (_a, _b, nodeA, nodeB) =>
          (nodeA.data?.visited_families ?? 0) - (nodeB.data?.visited_families ?? 0),
      },
      {
        headerName: t("coverage"),
        field: "coverage",
        width: 160,
        sortable: true,
        sort: "desc",
        cellRenderer: CoverageCell,
      },
    ],
    [t]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({ resizable: false, suppressMovable: true }),
    []
  );

  return (
    <div className="ag-theme-quartz" style={{ height: "56vh", width: "100%" }}>
      <AgGridReact<FlatRow>
        ref={gridRef}
        rowData={rows}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        quickFilterText={searchText}
        getRowId={(p) => p.data.part}
        animateRows
        rowHeight={44}
        headerHeight={48}
        suppressCellFocus
      />
    </div>
  );
});
